import saveIcon from "@assets/images/save-icon-white.svg";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Radio,
  RadioGroup
} from "@material-ui/core";
import { CloseOutlined } from "@material-ui/icons";
import InputSuggestion from "@saleor/alternatedMaterial/components/InputSuggestionForPrioridy";
import { isAuthTokenExpired, useUser } from "@saleor/auth";
import { useUserPermissions } from "@saleor/auth/hooks/useUserPermissions";
import { Zone } from "@saleor/auth/types";
import Checkbox from "@saleor/components/Checkbox";
import MyTypography from "@saleor/components/MyTypography";
import { SingleSelectField } from "@saleor/components/SingleSelectField";
import StaticInputSuggestion from "@saleor/components/StaticInputSuggestion";
import {
  PermissionEnum,
  useScgpMeQuery,
  useSearchSuggestionDomesticSoldTosLazyQuery
} from "@saleor/graphql";
import { makeStyles } from "@saleor/macaw-ui";
import { mapEdgesToItems } from "@saleor/utils/maps";
import uniqBy from "lodash/uniqBy";
import React, { useEffect, useMemo, useState } from "react";
import SVG from "react-inlinesvg";
import { useIntl } from "react-intl";

import { ConsignmentType } from "../ItemDetailsTabEdit/types";

interface InitialValueProps {
  inquiryMethod: string;
  changeConsignmentCode: boolean;
  ConsignmentType: ConsignmentType;
  consignmentLocation: string;
}

export interface RouterProps {
  open: boolean;
  orderId?: string | null;
  onClose: () => void;
  onSave: (value: InitialValueProps) => void;
  initialValue?: InitialValueProps;
  loading?: boolean;
  allowAccordingToIplan?: boolean;
  modalData?: any;
}

enum roleUser {
  EXPORT = "EXPORT",
  DOMESTIC = "DOMESTIC"
}

const useStyles = makeStyles(
  theme => ({
    root: {
      "& .MuiPaper-root": {
        minWidth: 400,
        boxShadow: "0px 4px 16px rgba(0, 0, 0, 0.25)",
        borderRadius: 16,
        padding: 24,
        overflow: "auto"
      },
      "& .MuiDialogActions-root": {
        borderTop: 0,
        padding: "24px 0 0 0",
        gap: 24
      }
    },
    dialogTitle: {
      padding: 0,
      "& .MuiTypography-root": {
        display: "flex",
        alignItems: "center"
      }
    },
    mainTitle: {
      color: theme.palette.info.main
    },
    closeIcon: {
      marginLeft: "auto",
      cursor: "pointer"
    },
    dialogCenter: {
      padding: "0px !important",
      marginTop: "24px !important",
      "& .MuiFormControl-root": {
        height: 40
      },
      "& .MuiSelect-root b": {
        fontWeight: 400,
        fontSize: "1.6rem"
      }
    },
    buttonAction: {
      width: "50%",
      height: 48,
      "& svg": {
        marginRight: 10
      }
    },
    nameField: {
      marginBottom: 10
    },
    size16: {
      fontSize: "1.6rem"
    },
    boxBackground: {
      borderRadius: 4,
      background: theme.palette.background.default,
      padding: "9px 16px",
      height: 40
    },
    radioGroup: {
      flexDirection: "row"
    },
    checkboxBackground: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      background: "#D4F4F8",
      padding: "0px 10px",
      marginTop: 20,
      fontSize: "1.6rem",
      borderRadius: "4px 4px 0px 0px",
      border: `1px solid ${theme.palette.grey[300]}`,
      borderBottom: 0
    },
    checkboxBefore: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      background: "#ffffff",
      borderRadius: "4px",
      border: `1px solid ${theme.palette.grey[300]}`,
      padding: "0px 10px",
      marginTop: 20,
      fontSize: "1.6rem"
    },
    background: {
      background: "#F2FAFB !important",
      padding: "9px 12px",
      borderRadius: "0px 0px 4px 4px",
      border: `1px solid ${theme.palette.grey[300]}`,
      borderTop: 0
    },
    textError: {
      color: theme.palette.error.light,
      marginTop: 5,
      fontSize: 12
    },
    required: {
      color: theme.palette.error.main,
      marginLeft: 1,
      "& :after": {
        content: "*"
      }
    },
    selectField: {
      height: "36px",
      "& .MuiSelect-root": {
        padding: "0.8rem 1.2rem",
        color: theme.palette.grey[800],
        display: "flex"
      }
    }
  }),
  { name: "ModalResultRoute" }
);

const ModalResultRouter: React.FC<RouterProps> = props => {
  const {
    open,
    onClose,
    onSave,
    initialValue,
    loading,
    allowAccordingToIplan = true,
    modalData
  } = props;
  const classes = useStyles();
  const intl = useIntl();
  const permissions = useUserPermissions();
  const [dataValues, setDataValues] = useState(initialValue);
  const [addedSaleGroupValue, setAddedSaleGroupValue] = useState("");
  const isChangeOrder = location.pathname.includes("change-order");
  const [
    getSoldToDataSuggestions,
    { data: fetchSoldToDataSuggestions, loading: loadingSoldToDataSuggestion }
  ] = useSearchSuggestionDomesticSoldTosLazyQuery({ fetchPolicy: "no-cache" });

  const { data: dataScgpMe } = useScgpMeQuery({
    skip: isAuthTokenExpired()
  });

  useEffect(() => {
    if (open) {
      if (initialValue) {
        const dataList = {
          inquiryMethod: isChangeOrder
            ? modalData?.inquiryMethod || initialValue.inquiryMethod
            : initialValue,
          changeConsignmentCode:
            modalData?.changeConsignmentCode ||
            initialValue.changeConsignmentCode,
          ConsignmentType:
            modalData?.ConsignmentType || initialValue.ConsignmentType,
          consignmentLocation:
            modalData?.consignmentLocation || initialValue.consignmentLocation,
          consignmenTitle: modalData?.consignmentTitle || ""
        };
        setDataValues(dataList);
      } else {
        if (getRoleUser() === roleUser.DOMESTIC) {
          setDataValues({ ...dataValues, inquiryMethod: "Domestic" });
        } else if (getRoleUser() === roleUser.EXPORT) {
          setDataValues({ ...dataValues, inquiryMethod: "Export" });
        }
      }
    }
  }, [open]);

  const { zone } = useUser();
  const extensionsPermissionExport = permissions?.find(
    perm => perm.code === PermissionEnum.VIEW_ORDER_EXPORT
  );
  const extensionsPermissionDomestic = permissions?.find(
    perm => perm.code === PermissionEnum.VIEW_ORDER_DOMESTIC
  );
  const getRoleUser = () => {
    if (
      !(extensionsPermissionExport && extensionsPermissionDomestic)
        ? extensionsPermissionExport && !extensionsPermissionDomestic
        : zone === Zone.Export
    ) {
      return roleUser.EXPORT;
    } else if (
      !(extensionsPermissionExport && extensionsPermissionDomestic)
        ? !extensionsPermissionExport && extensionsPermissionDomestic
        : zone === Zone.Domestic
    ) {
      return roleUser.DOMESTIC;
    } else {
      return roleUser.DOMESTIC;
    }
  };

  const inputChoices = useMemo(() => {
    if (getRoleUser() === roleUser.DOMESTIC) {
      return [
        {
          label: <b className={classes.size16}>Domestic</b>,
          value: "Domestic"
        },
        {
          label: <b className={classes.size16}>ASAP (Clear stock)</b>,
          value: "ASAP"
        }
      ];
    } else if (getRoleUser() === roleUser.EXPORT) {
      return [
        {
          label: <b className={classes.size16}>Export</b>,
          value: "Export"
        },
        {
          label: <b className={classes.size16}>ASAP (Clear stock)</b>,
          value: "ASAP"
        }
      ];
    }
    return [];
  }, [getRoleUser()]);

  // useEffect(() => {
  //   if (initialValue) {
  //     setSelectValue(initialValue);
  //   }
  // }, [initialValue]);

  const handleChangeValue = e => {
    const { name, value } = e.target;
    let tempvalues;
    tempvalues = { ...dataValues, [name]: value || e.target.checked };
    if (name === "changeConsignmentCode") {
      tempvalues.ConsignmentType = ConsignmentType.CUSTOMER;
      tempvalues.consignmentLocation = "";
    }
    if (
      name === "consignmentLocation" &&
      tempvalues.ConsignmentType === ConsignmentType.CUSTOMER
    ) {
      tempvalues = {
        ...dataValues,
        consignmentLocation: value?.code || "",
        consignmentTitle: value?.title || ""
      };
    }
    setDataValues(tempvalues);
  };

  const soldToOptions = useMemo(() => {
    let data = [];
    if (fetchSoldToDataSuggestions?.searchSuggestionDomesticSoldTos) {
      data = mapEdgesToItems(
        fetchSoldToDataSuggestions?.searchSuggestionDomesticSoldTos
      );
    }
    const res = data.map(data => ({
      title: data.displayText,
      value: data.code,
      code: data.code
    }));
    return uniqBy(res, "value");
  }, [fetchSoldToDataSuggestions?.searchSuggestionDomesticSoldTos]);

  const saleGroupChoice =
    dataScgpMe?.scgpMe?.extendData?.scgpSalesGroups?.map(item => ({
      title: `${item?.code} - ${item?.name}`,
      value: item?.code
    })) || [];

  const saleGroupValue = saleGroupChoice.find(
    x => x?.value === dataValues?.consignmentLocation
  );

  const handleAddedValue = e => {
    setAddedSaleGroupValue(e);
  };

  const suggestedResult = saleGroupChoice?.filter((option, index, self) => {
    const searchValue = addedSaleGroupValue?.toLowerCase();
    const isDuplicate =
      self.findIndex(
        item => item.value.toLowerCase() === option.value.toLowerCase()
      ) !== index;
    return (
      !isDuplicate &&
      (option.value.toLowerCase().includes(searchValue) ||
        option.title.toLowerCase().includes(searchValue))
    );
  });

  const soldToError =
    (!dataValues?.consignmentLocation && !modalData?.consignmentTitle) ||
    fetchSoldToDataSuggestions?.searchSuggestionDomesticSoldTos?.edges
      ?.length === 0;

  const soldToSaveError =
    !dataValues?.consignmentLocation ||
    (!dataValues?.consignmentLocation && !modalData?.consignmentTitle) ||
    fetchSoldToDataSuggestions?.searchSuggestionDomesticSoldTos?.edges
      ?.length === 0;

  const saleGroupError =
    !dataValues?.consignmentLocation || suggestedResult?.length === 0;

  const handleSave = () => {
    const { changeConsignmentCode } = dataValues;
    if (
      !changeConsignmentCode ||
      (changeConsignmentCode &&
        (dataValues?.ConsignmentType === ConsignmentType.CUSTOMER
          ? !soldToSaveError
          : !saleGroupError))
    ) {
      onSave(dataValues);
    }
  };

  const getErrorText = field => (
    <div className={classes.textError}>{field}</div>
  );

  return (
    <Dialog className={classes.root} open={open} onClose={onClose}>
      <DialogTitle className={classes.dialogTitle}>
        <MyTypography variant={"h6"} className={classes.mainTitle}>
          {intl.formatMessage({ defaultMessage: "Input Parameter" })}
        </MyTypography>
        <CloseOutlined
          id="closeInputParameterModal"
          data-testid="closeInputParameterModal"
          className={classes.closeIcon}
          onClick={onClose}
        />
      </DialogTitle>
      <DialogContent className={classes.dialogCenter}>
        <MyTypography variant={"subtitle3"} className={classes.nameField}>
          {intl.formatMessage({ defaultMessage: "Inquiry Method" })}
        </MyTypography>
        {allowAccordingToIplan ? (
          <SingleSelectField
            id="inquiryMethodDropdown"
            data-testid="inquiryMethodDropdown"
            showPlaceHolderWhenSelect
            placeholder={"Select"}
            choices={inputChoices}
            name="inquiryMethod"
            value={dataValues?.inquiryMethod}
            onChange={handleChangeValue}
          />
        ) : (
          <MyTypography variant="body3" className={classes.boxBackground}>
            {dataValues?.inquiryMethod}
          </MyTypography>
        )}
        {isChangeOrder && (
          <div>
            <MyTypography
              variant={"subtitle3"}
              className={
                dataValues?.changeConsignmentCode
                  ? classes.checkboxBackground
                  : classes.checkboxBefore
              }
            >
              {intl.formatMessage({
                defaultMessage: "Change Consignment Code"
              })}
              <Checkbox
                id="changeConsignmentCodeCheckbox"
                data-testid="changeConsignmentCodeCheckbox"
                name={`changeConsignmentCode`}
                size={"small"}
                checked={dataValues?.changeConsignmentCode}
                onChange={handleChangeValue}
                disabled={!allowAccordingToIplan}
              />
            </MyTypography>
            {dataValues?.changeConsignmentCode && (
              <div className={classes.background}>
                <MyTypography variant={"subtitle3"}>
                  {intl.formatMessage({ defaultMessage: "Consignment Type" })}
                </MyTypography>
                <RadioGroup
                  className={classes.radioGroup}
                  aria-labelledby="demo-controlled-radio-buttons-group"
                  name="ConsignmentType"
                  onChange={handleChangeValue}
                  value={dataValues?.ConsignmentType}
                >
                  {[
                    { label: "Customer", value: ConsignmentType.CUSTOMER },
                    { label: "Sale Group", value: ConsignmentType.SALE_GROUP }
                  ].map(option => (
                    <FormControlLabel
                      id={`radio${option.label}`}
                      data-testid={`radio${option.label}`}
                      key={`radio${option.value}`}
                      value={option.value}
                      control={<Radio />}
                      label={
                        <MyTypography variant={"body3"}>
                          {option.label}
                        </MyTypography>
                      }
                    />
                  ))}
                </RadioGroup>
                <MyTypography
                  variant={"subtitle3"}
                  className={classes.nameField}
                >
                  {intl.formatMessage({ defaultMessage: "Consignment Code" })}
                  <span className={classes.required}>*</span>
                </MyTypography>
                {dataValues?.changeConsignmentCode &&
                dataValues?.ConsignmentType === ConsignmentType.CUSTOMER ? (
                  <InputSuggestion
                    id="consignmentCodeSoldToSearch"
                    data-testid="consignmentCodeSoldToSearch"
                    contained
                    searchField="search"
                    dataOptions={soldToOptions}
                    fetchDataSuggestions={fetchSoldToDataSuggestions}
                    loadingDataSuggestion={loadingSoldToDataSuggestion}
                    getDataSuggestions={getSoldToDataSuggestions}
                    defaultValue={
                      dataValues?.changeConsignmentCode
                        ? modalData?.consignmentTitle
                        : ""
                    }
                    defaultInputValue={
                      dataValues?.changeConsignmentCode
                        ? modalData?.consignmentTitle
                        : ""
                    }
                    totalCount={
                      fetchSoldToDataSuggestions
                        ?.searchSuggestionDomesticSoldTos?.edges?.length
                    }
                    name={"consignmentLocation"}
                    placeholder="ค้นหา Sold To"
                    error={soldToError}
                    handleSelectedSearch={e => {
                      const selected = soldToOptions?.find(x => x.title === e);
                      handleChangeValue({
                        target: {
                          name: "consignmentLocation",
                          value: selected || ""
                        }
                      });
                    }}
                  />
                ) : (
                  <StaticInputSuggestion
                    id="consignmentCodeSaleGroupSearch"
                    contained
                    dataOptions={saleGroupChoice}
                    placeholder="ค้นหา Sale Group"
                    error={saleGroupError}
                    defaultValue={saleGroupValue?.title}
                    defaultInputValue={saleGroupValue?.title}
                    handleCheckAddedValue={handleAddedValue}
                    handleSelectedSearch={e => {
                      const selected = saleGroupChoice?.find(
                        x => x.title === e
                      );
                      handleChangeValue({
                        target: {
                          name: "consignmentLocation",
                          value: selected?.value || ""
                        }
                      });
                    }}
                  />
                )}
                {dataValues?.ConsignmentType === ConsignmentType.CUSTOMER &&
                  (fetchSoldToDataSuggestions?.searchSuggestionDomesticSoldTos
                    ?.edges?.length === 0
                    ? getErrorText("กรุณาระบุข้อมูลที่ถูกต้อง")
                    : !dataValues?.consignmentLocation &&
                      !modalData?.consignmentTitle
                    ? getErrorText("กรุณาระบุ")
                    : "")}
                {dataValues?.ConsignmentType === ConsignmentType.SALE_GROUP &&
                  (suggestedResult?.length === 0
                    ? getErrorText("กรุณาระบุข้อมูลที่ถูกต้อง")
                    : !dataValues?.consignmentLocation
                    ? getErrorText("กรุณาระบุ")
                    : "")}
              </div>
            )}
          </div>
        )}
      </DialogContent>
      <DialogActions>
        <Button
          id="closeInputParameterButton"
          data-testid="closeInputParameterButton"
          variant={"outlined"}
          color={"secondary"}
          className={classes.buttonAction}
          onClick={onClose}
        >
          <MyTypography variant={"body2"}>
            {intl.formatMessage({ defaultMessage: "ปิดหน้าต่าง" })}
          </MyTypography>
        </Button>
        <Button
          id="saveInputParameterButton"
          data-testid="saveInputParameterButton"
          variant={"contained"}
          color={"secondary"}
          onClick={handleSave}
          className={classes.buttonAction}
          disabled={!allowAccordingToIplan || loading}
        >
          <SVG src={saveIcon} />
          <MyTypography variant={"body2"}>
            {intl.formatMessage({ defaultMessage: "บันทึก" })}
          </MyTypography>
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ModalResultRouter;
